import type { GenreConfig } from './types';

export const ROCK: GenreConfig = {
  id: 'rock',
  siteName: 'RockAlbumReviews',
  domain: 'rockalbumreviews.com',
  noun: 'rock',
  label: 'Rock',
  shortName: 'RKAR',
  tagline: 'In-Depth Rock Album Reviews',
  description:
    'In-depth rock album reviews with a published scorecard: songwriting, production, performance and cohesion, from classic rock and punk to indie and alternative.',
  readerLine: 'a serious rock fan',
  xHandle: null,
  gaId: 'G-H6FD3NJD26',
  ahrefsKey: 'S5+gOLjnG/sxZ+s+LmvSkg',
  clarityId: 'z38qp8gxcb',
  accentHue: 25,

  dailyReviews: 1,
  launchReviews: 20,
  trackLimits: { min: 4, max: 30 },

  musicbrainzTags: ['rock', 'classic rock', 'alternative rock', 'indie rock', 'punk', 'hard rock', 'psychedelic rock', 'progressive rock'],
  wikipediaArtistSuffixes: ['(band)', '(musician)', '(singer)'],
  wikipediaArtistSearchTerm: 'rock band',
  artistKeywordSuffix: 'band',

  scorecard: {
    dimensions: [
      {
        key: 'writing',
        label: 'Songwriting',
        weight: 0.25,
        measures:
          'Melody, lyrics, song structure, hooks and riffs: whether the songs are built to last, with ideas that develop instead of repeating.',
      },
      {
        key: 'production',
        label: 'Production',
        weight: 0.25,
        measures:
          'Recording, arrangement and mix: how well the record is captured and how well the sound serves the songs.',
      },
      {
        key: 'delivery',
        label: 'Performance',
        weight: 0.15,
        measures:
          'Vocals, musicianship and band chemistry: the playing itself, from the conviction of the singer to how tightly the band locks in.',
      },
      {
        key: 'cohesion',
        label: 'Cohesion',
        weight: 0.15,
        measures:
          'Sequencing, pacing, length and filler: whether it works as an album rather than a pile of songs.',
      },
      {
        key: 'originality',
        label: 'Originality',
        weight: 0.1,
        measures:
          "What it adds, measured against the band's own catalog and the peers of its moment, not against what came later.",
      },
      {
        key: 'replay',
        label: 'Replay value',
        weight: 0.1,
        measures:
          'How it holds up on repeat listens and over time. For recent releases this is a projection and the note says so.',
      },
    ],
    topBandMeaning: 'Among the best rock albums ever made on this measure.',
  },

  canonicalGenres: [
    'Classic Rock',
    'Hard Rock',
    'Punk Rock',
    'Alternative Rock',
    'Indie Rock',
    'Psychedelic Rock',
    'Progressive Rock',
    'Grunge',
    'Post-Punk',
    'Garage Rock',
    'Art Rock',
    'Folk Rock',
  ],

  primaryGenres: [
    { label: 'Classic Rock', slug: 'classic-rock', hue: 25, styles: ['blues-rock', 'southern-rock', 'heartland-rock', 'arena-rock'] },
    { label: 'Hard Rock', slug: 'hard-rock', hue: 5, styles: ['glam-rock', 'glam-metal', 'stoner-rock', 'blues-rock'] },
    { label: 'Punk Rock', slug: 'punk-rock', hue: 350, styles: ['pop-punk', 'hardcore-punk', 'street-punk', 'oi'] },
    { label: 'Alternative Rock', slug: 'alternative-rock', hue: 200, styles: ['britpop', 'shoegaze', 'noise-rock', 'dream-pop'] },
    { label: 'Indie Rock', slug: 'indie-rock', hue: 160, styles: ['lo-fi', 'jangle-pop', 'math-rock', 'emo'] },
    { label: 'Psychedelic Rock', slug: 'psychedelic-rock', hue: 290, styles: ['acid-rock', 'space-rock', 'neo-psychedelia', 'krautrock'] },
    { label: 'Progressive Rock', slug: 'progressive-rock', hue: 230, styles: ['symphonic-prog', 'canterbury-scene', 'neo-prog', 'math-rock'] },
    { label: 'Grunge', slug: 'grunge', hue: 100, styles: ['post-grunge', 'sludge-rock', 'noise-rock'] },
    { label: 'Post-Punk', slug: 'post-punk', hue: 260, styles: ['new-wave', 'gothic-rock', 'post-punk-revival', 'darkwave'] },
    { label: 'Garage Rock', slug: 'garage-rock', hue: 45, styles: ['garage-rock-revival', 'proto-punk', 'surf-rock', 'blues-rock'] },
    { label: 'Art Rock', slug: 'art-rock', hue: 320, styles: ['experimental-rock', 'avant-rock', 'chamber-pop', 'glam-rock'] },
    { label: 'Folk Rock', slug: 'folk-rock', hue: 75, styles: ['country-rock', 'americana', 'singer-songwriter', 'psychedelic-folk'] },
  ],

  genreDescriptions: {
    'classic-rock':
      'The guitar-driven mainstream of the 1960s and 1970s. Classic rock grew from rock and roll and the blues and came into its own with The Rolling Stones, Led Zeppelin, Fleetwood Mac and Bruce Springsteen, in a period when the album became the main artistic unit.',
    'hard-rock':
      'Louder, heavier and built on the riff. Hard rock took shape in the late 1960s with Cream, Deep Purple and Led Zeppelin, carried on through AC/DC, Aerosmith and Van Halen, and stands behind the birth of heavy metal.',
    'punk-rock':
      'Short, fast and stripped back. Punk came together in the mid-1970s around the Ramones in New York and the Sex Pistols and The Clash in London, and its do-it-yourself approach shaped the independent music that followed.',
    'alternative-rock':
      'A broad label for guitar bands that grew up outside the mainstream. Alternative rock built its audience through the 1980s on college radio and independent labels with R.E.M., the Pixies and Sonic Youth, and broke through commercially in the early 1990s.',
    'indie-rock':
      'Guitar music defined by independent labels and a hands-on approach. Indie rock developed through the 1980s and 1990s with bands such as Pavement, Built to Spill and Modest Mouse, and covers everything from lo-fi recordings to carefully arranged studio work.',
    'psychedelic-rock':
      'Rock reshaped by studio experimentation and altered-state imagery. Psychedelic rock emerged in the mid-1960s with The Beatles, The Byrds, Pink Floyd and The Jimi Hendrix Experience, using feedback, effects and extended forms.',
    'progressive-rock':
      'Rock with longer forms, classical and jazz influence and conceptual ambition. Progressive rock flourished in Britain in the early 1970s with King Crimson, Yes, Genesis and Pink Floyd, and its ideas keep resurfacing in later bands.',
    grunge:
      'Heavy, distorted guitars with punk attitude and a bleak lyrical streak. Grunge came out of Seattle in the late 1980s and early 1990s with Nirvana, Pearl Jam, Soundgarden and Alice in Chains, and moved alternative rock into the mainstream.',
    'post-punk':
      'Punk energy pushed into new shapes. Post-punk emerged in the late 1970s with Joy Division, Gang of Four, Wire and Talking Heads, favoring angular guitars, dub-influenced bass and a willingness to experiment.',
    'garage-rock':
      'Raw, direct and recorded close to the room. Garage rock started with American teenage bands in the 1960s such as The Sonics and The Kingsmen, and was revived in the early 2000s by The White Stripes and The Strokes.',
    'art-rock':
      'Rock treated as an art form, with an eye on the studio, concept and image. Art rock draws on figures such as David Bowie, Roxy Music, Talking Heads and Radiohead, who pushed the form well beyond verse and chorus.',
    'folk-rock':
      'Folk songwriting with electric instrumentation. Folk rock took off in the mid-1960s when Bob Dylan went electric and The Byrds adapted folk songs, and it later fed into country rock and Americana through Neil Young, The Band and Fairport Convention.',
  },

  genreGuides: {
    'classic-rock': {
      sound:
        'Classic rock is built on guitar, bass and drums with a vocalist out front, and often keyboards for color. The sound ranges from blues-based grit to polished arena production, and the album as a statement is central to the genre.',
      listenFor:
        'Our scores weight songwriting and production most heavily, at 25% each, because a rock record lives or dies on its songs and on whether the recording captures the band. Albums with two huge singles and little else land in the middle bands; albums with great songs and a flat mix do too.',
    },
    'hard-rock': {
      sound:
        'Hard rock leans on loud, riff-driven guitar, a strong rhythm section and a big vocal. The riff is the hook, and the best records leave room for dynamics instead of running everything at one volume.',
      listenFor:
        'The scorecard asks whether the riffs are memorable, whether the production gives the guitars weight without burying the vocal, and whether the album sustains its energy. Attitude alone does not score.',
    },
    'punk-rock': {
      sound:
        'Punk rock is short songs, fast tempos, simple chords and a direct vocal. Rough production is often a choice, and urgency matters more than polish.',
      listenFor:
        'The scorecard judges sound against intent: a raw mix is not a flaw when it serves the record. Albums that turn energy into memorable songs score highest; those that rely on speed and attitude alone lose points on songwriting and replay.',
    },
    'alternative-rock': {
      sound:
        'Alternative rock covers a wide range, from jangling guitars and noisy distortion to atmospheric textures and big melodic hooks. What unites it is a distance from the mainstream rock formula of its time.',
      listenFor:
        'Originality and cohesion carry more weight than usual in this genre, because the best records have a clear identity from start to finish. The scorecard also checks whether the songs hold up once the sound of the moment fades.',
    },
  },

  styleExamples: [
    'blues rock', 'glam rock', 'pop punk', 'hardcore punk', 'britpop', 'shoegaze',
    'math rock', 'neo-psychedelia', 'symphonic prog', 'post-grunge', 'garage rock revival', 'americana',
  ],
  shortGenreSuffixes: [' Rock'],

  prompt: {
    evidenceExamples: 'a riff, a vocal take, a chorus, a drum fill, a production choice, a sequencing decision',
    fillerTracks: 'intros, interludes, skits and reprises',
    analysisFocus: 'songwriting, production and performance. Name producers and engineers only from the FACT SHEET.',
    primaryKeywordExample: '"Led Zeppelin IV review" or "Radiohead OK Computer review"',
    secondaryKeywordExample: '"OK Computer album analysis", "best alternative rock albums", "Radiohead third album"',
    headingExamples:
      '"Nirvana Nevermind — Punk Writes the Hook Pop Never Could", "Radiohead OK Computer — Where Guitar Rock Found Its Anxiety"',
  },

  voices: [
    {
      name: 'Walter',
      title: 'Lineage Voice',
      initials: 'W',
      bio: 'The house voice for rock history: where an album sits in the lineage from rock and roll onward, who it learned from and who learned from it.',
      specialization: 'Lineage',
      featured: true,
      avatarHue: 25,
      lens: `LENS: Lineage
Place the album in rock history: what it answered, which bands it learned from, which bands learned from it.
Voice: direct, confident, willing to disagree with the consensus.`,
      match: {
        artists: ['the rolling stones', 'led zeppelin', 'the who', 'the kinks', 'chuck berry', 'little richard', 'elvis presley', 'buddy holly', 'the beach boys', 'creedence clearwater revival', 'the doors', 'cream', 'the beatles', 'fleetwood mac', 'eric clapton', 'the animals', 'small faces', 'free', 'the yardbirds', 'bob seger'],
        labels: ['atlantic', 'decca', 'columbia', 'island', 'chess'],
        keywords: ['classic rock', 'rock and roll', 'sixties', 'seventies', 'blues rock', 'lineage'],
      },
    },
    {
      name: 'Oliver',
      title: 'British Rock Voice',
      initials: 'O',
      bio: 'Listens from Britain: the invasion bands, glam, britpop and the island scenes that kept reshaping rock, and what stayed distinctly British.',
      specialization: 'British',
      featured: true,
      avatarHue: 210,
      lens: `LENS: British rock
Hear the album against the British tradition: beat groups, glam, punk, indie and britpop. Notice what is local, what is borrowed from America, and what travelled back.
Voice: dry, precise and comparative.`,
      match: {
        artists: ['the kinks', 'the who', 'david bowie', 't. rex', 'roxy music', 'queen', 'oasis', 'blur', 'pulp', 'suede', 'the verve', 'the smiths', 'the stone roses', 'arctic monkeys', 'the cure', 'happy mondays', 'supergrass', 'elbow', 'manic street preachers', 'primal scream'],
        labels: ['parlophone', 'creation', 'rough trade', 'factory', 'island', 'domino'],
        keywords: ['british', 'britpop', 'london', 'manchester', 'glam', 'uk'],
      },
    },
    {
      name: 'Hugo',
      title: 'Studio & Production Voice',
      initials: 'H',
      bio: 'Listens production-first: recording choices, arrangement, the mix and the producers and engineers who shaped how rock records sound.',
      specialization: 'Studio',
      featured: true,
      avatarHue: 160,
      lens: `LENS: The studio
Listen to the album as a recording: tones, the room, arrangement choices, mixing and dynamics. Treat producers and engineers named in the FACT SHEET as co-authors.
Voice: precise and practical, technical without jargon for its own sake.`,
      match: {
        artists: ['pink floyd', 'steely dan', 'fleetwood mac', 'queen', 'electric light orchestra', 'the alan parsons project', 'tame impala', 'radiohead', 'foo fighters', 'weezer', 'my bloody valentine', 'beck', 'the flaming lips', 'brian eno', 'def leppard', 'phil spector'],
        labels: ['geffen', 'sub pop', 'dgc', 'warner bros'],
        keywords: ['production', 'producer', 'remaster', 'mix', 'studio', 'recording', 'engineer'],
      },
    },
    {
      name: 'Nora',
      title: 'Songwriting & Lyrics Voice',
      initials: 'N',
      bio: 'Reads the songwriting closely: melody, structure, hooks and lyrics, and whether the songs develop their ideas or just repeat them.',
      specialization: 'Songwriting',
      featured: true,
      avatarHue: 330,
      lens: `LENS: The songwriting
Study how the songs are built: melody, chord movement, structure, hooks and where a song develops an idea instead of restating it. Paraphrase the lyrics; never quote them.
Voice: incisive and analytical, never decorative.`,
      match: {
        artists: ['bob dylan', 'the beatles', 'leonard cohen', 'elvis costello', 'paul simon', 'joni mitchell', 'neil young', 'lou reed', 'the velvet underground', 'patti smith', 'tom petty', 'the replacements', 'big star', 'r.e.m.', 'wilco', 'the national', 'the hold steady', 'father john misty'],
        labels: ['asylum', 'reprise', 'elektra', 'matador', 'merge'],
        keywords: ['songwriting', 'lyrics', 'melody', 'hook', 'songwriter', 'storytelling'],
      },
    },
    {
      name: 'Dale',
      title: 'American Roots & Heartland Voice',
      initials: 'D',
      bio: 'Covers American rock from the roots outward: blues, country, folk and the heartland tradition, and how the road and the small town shaped the songs.',
      specialization: 'Heartland',
      featured: false,
      avatarHue: 40,
      lens: `LENS: American roots and heartland
Hear the album against the American tradition: blues, country, folk, southern and heartland rock. Know what is earned and what is a pose.
Voice: warm but unsentimental, plainspoken.`,
      match: {
        artists: ['bruce springsteen', 'tom petty', 'john mellencamp', 'bob seger', 'creedence clearwater revival', 'lynyrd skynyrd', 'the allman brothers band', 'neil young', 'the band', 'the byrds', 'steve earle', 'wilco', 'uncle tupelo', 'drive-by truckers', 'ryan adams', 'the black keys', 'jason isbell', 'gram parsons'],
        labels: ['columbia', 'capricorn', 'asylum', 'geffen', 'nonesuch'],
        keywords: ['heartland', 'southern', 'americana', 'country rock', 'roots', 'folk rock', 'blues'],
      },
    },
    {
      name: 'Rory',
      title: 'Punk & Post-Punk Voice',
      initials: 'R',
      bio: 'Covers punk and what came after it: the 1970s scenes, hardcore, post-punk and the do-it-yourself ethic that shaped independent rock.',
      specialization: 'Punk',
      featured: false,
      avatarHue: 350,
      lens: `LENS: Punk and post-punk
Hear the album against punk and its aftermath: urgency, economy, scene and the question of what it dared to change. Do not mistake noise for conviction.
Voice: brisk, skeptical, allergic to nostalgia.`,
      match: {
        artists: ['ramones', 'sex pistols', 'the clash', 'the damned', 'buzzcocks', 'television', 'wire', 'gang of four', 'joy division', 'new order', 'talking heads', 'the fall', 'bad brains', 'black flag', 'minor threat', 'dead kennedys', 'hüsker dü', 'fugazi', 'the stooges', 'the slits'],
        labels: ['stiff', 'sire', 'factory', 'rough trade', 'dischord', 'sst', 'epitaph'],
        keywords: ['punk', 'post-punk', 'hardcore', 'new wave', 'diy', 'scene'],
      },
    },
    {
      name: 'Casey',
      title: 'Alternative, Indie & Grunge Voice',
      initials: 'C',
      bio: 'Follows the guitar underground: college radio, independent labels, grunge and indie, and how the margins became the center.',
      specialization: 'Alternative',
      featured: false,
      avatarHue: 190,
      lens: `LENS: Alternative, indie and grunge
Hear the album as part of the independent tradition: college radio, small labels, lo-fi and the major-label moment of the 1990s. Judge songs and identity, not scene credentials.
Voice: wry, informed and unimpressed by hype.`,
      match: {
        artists: ['nirvana', 'pearl jam', 'soundgarden', 'alice in chains', 'pixies', 'sonic youth', 'dinosaur jr.', 'pavement', 'sebadoh', 'built to spill', 'modest mouse', 'yo la tengo', 'sleater-kinney', 'smashing pumpkins', 'mudhoney', 'the breeders', 'guided by voices', 'slint'],
        labels: ['sub pop', 'matador', 'merge', '4ad', 'touch and go', 'kill rock stars', 'sst', 'dgc'],
        keywords: ['grunge', 'indie', 'alternative', 'lo-fi', 'seattle', 'college radio', 'shoegaze'],
      },
    },
    {
      name: 'Jonah',
      title: 'Modern Era Voice',
      initials: 'J',
      bio: 'Covers rock since 2005: garage revival, indie-streaming crossover and the bands still working in a genre that no longer owns the charts, and is the voice most likely to push back on the canon.',
      specialization: 'Modern',
      featured: false,
      avatarHue: 120,
      lens: `LENS: The modern era
Test the canon instead of deferring to it. Understand how streaming, home recording and the shrinking rock audience changed what a rock album is for; judge new records on their own terms.
Voice: sharp, contrarian when the evidence supports it.`,
      match: {
        artists: ['arcade fire', 'the strokes', 'arctic monkeys', 'tame impala', 'fontaines d.c.', 'idles', 'big thief', 'phoebe bridgers', 'the war on drugs', 'vampire weekend', 'queens of the stone age', 'the black keys', 'wet leg', 'black midi', 'turnstile', 'japanese breakfast', 'mitski', 'king gizzard & the lizard wizard'],
        labels: ['domino', 'matador', 'sub pop', 'dead oceans', 'partisan', 'rough trade', 'xl recordings'],
        keywords: ['modern', 'streaming', 'revival', 'bedroom', 'post-punk revival', 'indie'],
        yearAfter: 2005,
      },
    },
    {
      name: 'Felicity',
      title: 'Prog & Art Rock Voice',
      initials: 'F',
      bio: 'Follows progressive and art rock: long-form structure, concept albums and records that treat the studio as an instrument, and when ambition pays off.',
      specialization: 'Prog & Art',
      featured: false,
      avatarHue: 260,
      lens: `LENS: Progressive and art rock
Read the album as design: structure across songs, concept, arrangement and the use of the studio. Ask whether the ambition serves the music or just displays it.
Voice: strategic and analytical.`,
      match: {
        artists: ['pink floyd', 'king crimson', 'yes', 'genesis', 'rush', 'jethro tull', 'emerson, lake & palmer', 'gentle giant', 'camel', 'david bowie', 'roxy music', 'talking heads', 'radiohead', 'porcupine tree', 'can', 'neu!', 'brian eno', 'peter gabriel'],
        labels: ['harvest', 'charisma', 'virgin', 'emi', 'island'],
        keywords: ['progressive', 'prog', 'concept', 'art rock', 'experimental', 'krautrock', 'symphonic'],
      },
    },
    {
      name: 'Marta',
      title: 'Social Context Voice',
      initials: 'M',
      bio: 'Reads albums for the world around them: politics, youth culture, censorship and the social moment that produced the record.',
      specialization: 'Context',
      featured: false,
      avatarHue: 85,
      lens: `LENS: Social context
Read the album as a statement of its time: the politics, youth culture and economic moment around it, and whether the words match the weight of the music. Paraphrase; never quote lyrics.
Voice: contextual and accessible, never academic for its own sake.`,
      match: {
        artists: ['the clash', 'bob dylan', 'creedence clearwater revival', 'rage against the machine', 'u2', 'bruce springsteen', 'the jam', 'public enemy', 'green day', 'neil young', 'crass', 'the specials', 'billy bragg', 'pussy riot', 'patti smith', 'fugazi', 'dead kennedys', 'sleater-kinney'],
        labels: ['epic', 'columbia', 'island', '2 tone', 'rough trade'],
        keywords: ['political', 'protest', 'social', 'war', 'youth', 'working class', 'censorship'],
      },
    },
  ],

  gapTemplates: {
    bestOfYear: 'Best Rock Albums of {year}',
    era: 'Rock in the {era}',
    subgenresExplained: 'Rock Subgenres Explained',
  },
};
